import React, { useState, useMemo } from "react";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import { Link } from "react-router-dom";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

import StorefrontIcon from "@mui/icons-material/StorefrontOutlined";
import FactoryIcon from "@mui/icons-material/FactoryOutlined";
import DomainIcon from "@mui/icons-material/DomainOutlined";
import SearchIcon from "@mui/icons-material/SearchOutlined";
import FilterAltIcon from "@mui/icons-material/FilterAltOutlined";
import LightModeIcon from "@mui/icons-material/LightModeOutlined";
import DarkModeIcon from "@mui/icons-material/DarkModeOutlined";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import MyLocationIcon from "@mui/icons-material/MyLocationOutlined";
import LayersIcon from "@mui/icons-material/LayersOutlined";
import CloseIcon from "@mui/icons-material/CloseOutlined";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import PublicIcon from "@mui/icons-material/Public";
import LoginOutlinedIcon from "@mui/icons-material/LoginOutlined";
import ArrowOutwardOutlinedIcon from "@mui/icons-material/ArrowOutwardOutlined";

import logoMinisterio from "../assets/logo_ministerio.png";
import logoPndp from "../assets/pndp.png";

import "../styles/tokens.css";
import "../styles/mici-ui.css";
import { AppConfig } from "../config/appConfig";

const LAYERS = [
  { id: "mercados", label: "Mercados Minoristas",  short: "Mercados", color: "#14B8A6", Icon: StorefrontIcon, count: 247 },
  { id: "parques",  label: "Parques Industriales", short: "Parques",  color: "#F59E0B", Icon: FactoryIcon,    count: 38  },
  { id: "zonas",    label: "Zonas Industriales",   short: "Zonas",    color: "#8B5CF6", Icon: DomainIcon,     count: 19  },
];

const SAMPLE = [
  { id: 1, type: "mercados", name: "Mercado de Surquillo Nº1",   dist: "Surquillo",  prov: "Lima",    cui: "2625481", estado: "En ejecución", modalidad: "Asistencia Técnica",            lat: -12.115, lng: -77.020 },
  { id: 2, type: "mercados", name: "Mercado Modelo Chorrillos",  dist: "Chorrillos", prov: "Lima",    cui: "2604188", estado: "Culminado",    modalidad: "Elaborado por PNDP",            lat: -12.180, lng: -77.024 },
  { id: 3, type: "parques",  name: "Parque Industrial Lurín",    dist: "Lurín",      prov: "Lima",    cui: "2581002", estado: "Planeamiento", modalidad: "Convenio PNDP-Municip. Dist.",  lat: -12.275, lng: -76.870 },
  { id: 4, type: "zonas",    name: "Zona Industrial Callao",     dist: "Callao",     prov: "Callao",  cui: "2549107", estado: "En ejecución", modalidad: "Sin convenio",                  lat: -12.050, lng: -77.118 },
  { id: 5, type: "mercados", name: "Mercado Central de Lima",    dist: "Cercado",    prov: "Lima",    cui: "2519045", estado: "Culminado",    modalidad: "Asistencia Técnica",            lat: -12.046, lng: -77.030 },
  { id: 6, type: "parques",  name: "Parque Industrial Ancón",    dist: "Ancón",      prov: "Lima",    cui: "2598430", estado: "En ejecución", modalidad: "Convenio PNDP-Municip. Prov.",  lat: -11.770, lng: -77.180 },
];

const TILE_DARK  = AppConfig.TILE_CARTO_DARK;
const TILE_LIGHT = AppConfig.TILE_CARTO_LIGHT;

const ESTADO_BADGE = {
  "En ejecución": { bg: "rgba(245, 158, 11, 0.18)", color: "#FCD34D" },
  "Culminado":    { bg: "rgba(16, 185, 129, 0.18)", color: "#6EE7B7" },
  "Planeamiento": { bg: "rgba(59, 130, 246, 0.18)", color: "#93C5FD" },
};

const makeIcon = (color) =>
  L.divIcon({
    className: "px-marker",
    html: `<span class="px-marker-ring" style="--c:${color}"></span><span class="px-marker-dot" style="background:${color}"></span>`,
    iconSize: [28, 28],
    iconAnchor: [14, 14],
    popupAnchor: [0, -14],
  });

const ICONS_BY_TYPE = LAYERS.reduce((acc, l) => {
  acc[l.id] = makeIcon(l.color);
  return acc;
}, {});

const Preview = () => {
  const [theme, setTheme] = useState("dark");
  const [activeLayer, setActiveLayer] = useState("mercados");
  const [panelOpen, setPanelOpen] = useState(true);
  const [estadoFilter, setEstadoFilter] = useState(new Set(["En ejecución"]));

  const activeLayerObj = LAYERS.find((l) => l.id === activeLayer);
  const filteredPoints = useMemo(
    () => SAMPLE.filter((p) => p.type === activeLayer),
    [activeLayer]
  );

  const toggleEstado = (e) => {
    setEstadoFilter((prev) => {
      const next = new Set(prev);
      if (next.has(e)) next.delete(e);
      else next.add(e);
      return next;
    });
  };

  return (
    <div className="px-root" data-theme={theme}>
      <MapContainer
        center={[-12.10, -77.04]}
        zoom={11}
        zoomControl={false}
        attributionControl={false}
        className="px-map"
      >
        <TileLayer url={theme === "dark" ? TILE_DARK : TILE_LIGHT} />
        {filteredPoints.map((p) => {
          const layer = LAYERS.find((x) => x.id === p.type);
          const Icon = layer.Icon;
          const estadoStyle = ESTADO_BADGE[p.estado];
          return (
            <Marker
              key={p.id}
              position={[p.lat, p.lng]}
              icon={ICONS_BY_TYPE[p.type]}
            >
              <Popup>
                <div className="px-popup">
                  <div className="px-popup-head">
                    <span className="px-tag" style={{ "--c": layer.color }}>
                      <Icon style={{ fontSize: 14 }} /> {layer.short}
                    </span>
                    <span
                      className="px-badge"
                      style={{ background: estadoStyle.bg, color: estadoStyle.color }}
                    >
                      {p.estado}
                    </span>
                  </div>
                  <h3 className="px-popup-title">{p.name}</h3>
                  <div className="px-popup-sub">{p.dist} · {p.prov}</div>
                  <div className="px-popup-grid">
                    <div>
                      <span>CUI</span>
                      <strong>{p.cui}</strong>
                    </div>
                    <div>
                      <span>Modalidad</span>
                      <strong>{p.modalidad}</strong>
                    </div>
                  </div>
                  <button className="px-btn px-btn-primary px-btn-sm">
                    Ver detalle completo
                  </button>
                </div>
              </Popup>
            </Marker>
          );
        })}
      </MapContainer>

      {/* STRIP INSTITUCIONAL */}
      <div className="px-strip">
        <img
          src={logoMinisterio}
          alt="Ministerio de la Producción"
          className="px-strip-logo px-strip-logo--produce"
        />
        <span className="px-strip-tag">
          Plataforma oficial del Programa Nacional de Diversificación Productiva
        </span>
        <img
          src={logoPndp}
          alt="PNDP"
          className="px-strip-logo px-strip-logo--pndp"
        />
      </div>

      {/* HEADER */}
      <header className="px-header">
        <div className="px-header-left">
          <Link to="/" className="px-back" title="Volver a la versión actual">
            <ArrowBackIcon style={{ fontSize: 18 }} />
          </Link>
          <div className="px-brand">
            <div className="px-brand-mark" title="Centrar mapa en Perú">
              <PublicIcon style={{ fontSize: 20, color: "white" }} />
            </div>
            <div className="px-brand-text">
              <strong>MICI</strong>
              <span>Mapa Interactivo de Comercio Interno e Industria</span>
            </div>
          </div>
        </div>

        <nav className="px-tabs">
          {LAYERS.map((l) => {
            const Icon = l.Icon;
            const active = activeLayer === l.id;
            return (
              <button
                key={l.id}
                className={`px-tab ${active ? "is-active" : ""}`}
                style={active ? { "--c": l.color } : undefined}
                onClick={() => setActiveLayer(l.id)}
              >
                <Icon style={{ fontSize: 16 }} />
                <span>{l.short}</span>
                <em>{l.count}</em>
              </button>
            );
          })}
        </nav>

        <div className="px-toolbar">
          <button
            className="px-icon-btn"
            onClick={() => setTheme((t) => (t === "dark" ? "light" : "dark"))}
            title={`Cambiar a tema ${theme === "dark" ? "claro" : "oscuro"}`}
          >
            {theme === "dark" ? (
              <LightModeIcon fontSize="small" />
            ) : (
              <DarkModeIcon fontSize="small" />
            )}
          </button>
          <button className="px-icon-btn" title="Buscar">
            <SearchIcon fontSize="small" />
          </button>
          <button
            type="button"
            className="px-staff-btn"
            onClick={() => {
              window.location.href = AppConfig.PORTAL_URL;
            }}
            title="Portal oficial de funcionarios PRODUCE"
          >
            <LoginOutlinedIcon
              className="px-staff-btn-icon"
              style={{ fontSize: 18 }}
            />
            <span>Acceso funcionarios</span>
            <ArrowOutwardOutlinedIcon
              className="px-staff-btn-arrow"
              style={{ fontSize: 14 }}
            />
          </button>
        </div>
      </header>

      {/* FILTER PANEL */}
      <aside className={`px-panel ${panelOpen ? "" : "is-collapsed"}`}>
        <div className="px-panel-handle" onClick={() => setPanelOpen((o) => !o)}>
          <ChevronLeftIcon fontSize="small" />
        </div>
        <div className="px-panel-inner">
          <div className="px-panel-header">
            <div className="px-panel-title">
              <FilterAltIcon fontSize="small" />
              <h2>Filtros</h2>
              <span className="px-pill" style={{ "--c": activeLayerObj.color }}>
                {activeLayerObj.count} resultados
              </span>
            </div>
            <button
              className="px-icon-btn px-icon-btn-sm"
              onClick={() => setPanelOpen(false)}
              title="Cerrar panel"
            >
              <CloseIcon style={{ fontSize: 16 }} />
            </button>
          </div>

          <div className="px-search">
            <SearchIcon style={{ fontSize: 16 }} />
            <input placeholder={`Buscar en ${activeLayerObj.short.toLowerCase()}…`} />
          </div>

          <div className="px-section">
            <h3>Ubicación</h3>
            <div className="px-select">
              <span>Departamento</span>
              <strong>Todos</strong>
              <KeyboardArrowDownIcon style={{ fontSize: 16, color: "var(--text-muted)" }} />
            </div>
            <div className="px-select">
              <span>Provincia</span>
              <strong>Lima</strong>
              <KeyboardArrowDownIcon style={{ fontSize: 16, color: "var(--text-muted)" }} />
            </div>
            <div className="px-select">
              <span>Distrito</span>
              <strong>Todos</strong>
              <KeyboardArrowDownIcon style={{ fontSize: 16, color: "var(--text-muted)" }} />
            </div>
          </div>

          <div className="px-section">
            <h3>Estado de intervención</h3>
            <div className="px-chips">
              {["En ejecución", "Culminado", "Planeamiento"].map((e) => {
                const selected = estadoFilter.has(e);
                return (
                  <button
                    key={e}
                    className={`px-chip ${selected ? "is-selected" : ""}`}
                    onClick={() => toggleEstado(e)}
                  >
                    <span
                      className="px-chip-dot"
                      style={{ background: ESTADO_BADGE[e].color }}
                    />
                    {e}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="px-section">
            <h3>Modalidad</h3>
            <label className="px-check">
              <input type="checkbox" defaultChecked />
              <span>Asistencia Técnica</span>
              <em>120</em>
            </label>
            <label className="px-check">
              <input type="checkbox" />
              <span>Elaborado por PNDP</span>
              <em>45</em>
            </label>
            <label className="px-check">
              <input type="checkbox" />
              <span>Convenio PNDP-Municip.</span>
              <em>82</em>
            </label>
          </div>

          <div className="px-panel-actions">
            <button className="px-btn">Limpiar</button>
            <button className="px-btn px-btn-primary">Aplicar filtros</button>
          </div>
        </div>
      </aside>

      {/* CONTROLES */}
      <div className="px-controls">
        <button className="px-ctrl" title="Acercar"><AddIcon fontSize="small" /></button>
        <button className="px-ctrl" title="Alejar"><RemoveIcon fontSize="small" /></button>
        <div className="px-ctrl-sep" />
        <button className="px-ctrl" title="Mi ubicación"><MyLocationIcon fontSize="small" /></button>
        <button className="px-ctrl" title="Capas base"><LayersIcon fontSize="small" /></button>
      </div>

      {/* LEYENDA */}
      <div className="px-legend">
        <div className="px-legend-head">
          <LayersIcon style={{ fontSize: 14 }} />
          <h3>Leyenda</h3>
        </div>
        {LAYERS.map((l) => (
          <div
            key={l.id}
            className={`px-legend-row ${activeLayer === l.id ? "is-active" : ""}`}
            onClick={() => setActiveLayer(l.id)}
            style={{ "--c": l.color }}
          >
            <span className="px-legend-dot" style={{ background: l.color }} />
            <span className="px-legend-label">{l.label}</span>
            <em>{l.count}</em>
          </div>
        ))}
      </div>

      {/* VERSION BADGE */}
      <div className="px-version-badge">
        <span className="px-dot" />
        Vista previa de diseño · v0.1
      </div>
    </div>
  );
};

export default Preview;
